import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, CircularProgress, Tooltip, Tabs, Tab, Button, Container, Dialog, DialogTitle, DialogContent,
    Grid, Card, CardContent, DialogActions, IconButton, CardActions
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import HomeLayout from '../../../components/layout/HomeLayout';
import orderService from '../../../services/orderService';
import paymentService from '../../../services/paymentService';
import subscriptionService from '../../../services/subscriptionService';
import PaymentMethodModal from '../components/PaymentMethodModal';
import { useNavigate } from 'react-router-dom';

const OrderHistoryPage = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState(0); // 0: Đơn hàng, 1: Giao dịch ví
    const [orders, setOrders] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Renewal & Payment State
    const [showRenewModal, setShowRenewModal] = useState(false);
    const [packages, setPackages] = useState([]);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [balance, setBalance] = useState(0);

    useEffect(() => {
        fetchData();
        fetchPackages();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [ordersRes, transRes, balanceRes] = await Promise.all([
                orderService.getMyOrders(),
                paymentService.getTransactions({ limit: 20 }),
                paymentService.getBalance()
            ]);

            if (ordersRes.success) setOrders(ordersRes.data);
            if (transRes.success) setTransactions(transRes.data);
            if (balanceRes.success) setBalance(Number(balanceRes.balance));
        } catch (error) {
            console.error('Failed to fetch history', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPackages = async () => {
        try {
            const res = await subscriptionService.getPackages();
            if (res.success) setPackages(res.data);
        } catch (error) {
            console.error('Failed to fetch packages', error);
        }
    };

    const formatDate = (dateString, includeSeconds = false) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const options = {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        };
        if (includeSeconds) options.second = '2-digit';
        return date.toLocaleString('vi-VN', options);
    };

    const calculateExpiry = (createdAt, durationDays) => {
        if (!createdAt || !durationDays) return null;
        const date = new Date(createdAt);
        date.setDate(date.getDate() + durationDays);
        return date;
    };

    const isExpired = (expiryDate) => {
        return expiryDate && new Date() > expiryDate;
    };

    const handleRenewClick = () => {
        setShowRenewModal(true);
    };

    const handleSelectPackage = (pkg) => {
        setSelectedPackage(pkg);
        setShowRenewModal(false);
        setShowPaymentModal(true);
    };

    const handlePaymentClose = () => {
        setShowPaymentModal(false);
    };

    const handlePaymentSuccess = () => {
        setShowPaymentModal(false);
        fetchData(); // Refresh orders
    };

    const getStatusChip = (status) => {
        const map = {
            PENDING: { label: 'Đang xử lý', color: 'warning' },
            COMPLETED: { label: 'Thành công', color: 'success' },
            CANCELLED: { label: 'Đã hủy', color: 'error' },
            FAILED: { label: 'Thất bại', color: 'error' },
            DEPOSIT: { label: 'Nạp tiền', color: 'info' },
            PAYMENT: { label: 'Thanh toán', color: 'default' },
            REFUND: { label: 'Hoàn tiền', color: 'secondary' }
        };
        const conf = map[status] || { label: status, color: 'default' };
        return <Chip label={conf.label} color={conf.color} size="small" />;
    };

    return (
        <HomeLayout>
            <Box sx={{ p: 3 }}>
                <Box mb={4} sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' }, 
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2
                }}>
                    <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff', fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.125rem' } }}>Lịch Sử Hoạt Động</Typography>
                        <Typography color="textSecondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>Theo dõi đơn hàng và biến động số dư</Typography>
                    </Box>
                    <Button startIcon={<RefreshIcon />} onClick={fetchData} sx={{ color: '#94a3b8' }}>
                        Làm mới
                    </Button>
                </Box>

                <Paper sx={{ bgcolor: '#1e293b', color: 'white' }}>
                    <Tabs
                        value={tab}
                        onChange={(e, v) => setTab(v)}
                        sx={{ borderBottom: 1, borderColor: 'rgba(255,255,255,0.1)' }}
                        textColor="primary"
                        indicatorColor="primary"
                    >
                        <Tab icon={<ReceiptLongIcon />} iconPosition="start" label="Đơn Hàng / Gia Hạn" />
                        <Tab icon={<HistoryIcon />} iconPosition="start" label="Lịch Sử Giao Dịch Ví" />
                    </Tabs>

                    <Box p={3}>
                        {loading ? (
                            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                        ) : tab === 0 ? (
                            // ORDER HISTORY TABLE
                            orders.length === 0 ? (
                                <Typography textAlign="center" color="textSecondary">Chưa có đơn hàng nào.</Typography>
                            ) : (
                                <TableContainer>
                                    <Table sx={{ minWidth: 700 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ color: '#94a3b8' }}>Mã Đơn</TableCell>
                                                <TableCell sx={{ color: '#94a3b8' }}>Gói Dịch Vụ</TableCell>
                                                <TableCell sx={{ color: '#94a3b8', display: { xs: 'none', md: 'table-cell' } }}>Ngày Hết Hạn</TableCell>
                                                <TableCell sx={{ color: '#94a3b8', display: { xs: 'none', sm: 'table-cell' } }}>Thanh Toán</TableCell>
                                                <TableCell sx={{ color: '#94a3b8' }}>Trạng Thái</TableCell>
                                                <TableCell sx={{ color: '#94a3b8' }} align="right">Hành Động</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {orders.map((order) => {
                                                let expiryDate = null;
                                                if (order.status === 'COMPLETED' && order.endDate) {
                                                    expiryDate = new Date(order.endDate);
                                                } else if (order.createdAt && order.snapshotDuration) {
                                                    // Fallback cho order PENDING/FAILED
                                                    expiryDate = calculateExpiry(order.createdAt, order.snapshotDuration);
                                                }
                                                const expired = isExpired(expiryDate);
                                                return (
                                                    <TableRow key={order.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                                                        <TableCell sx={{ color: 'white', fontFamily: 'monospace' }}>
                                                            #{order.id.slice(0, 8).toUpperCase()}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography fontWeight="bold" color="white">{order.snapshotName || 'Gói Dịch Vụ'}</Typography>
                                                            <Typography variant="caption" color="#94a3b8">
                                                                {Number(order.price).toLocaleString()}đ {order.snapshotDuration ? `/ ${order.snapshotDuration} ngày` : ''}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ color: 'white', display: { xs: 'none', md: 'table-cell' } }}>
                                                            {expiryDate ? (
                                                                <Box>
                                                                    <Typography variant="body2" color={expired ? 'error' : 'success'}>
                                                                        {formatDate(expiryDate)}
                                                                    </Typography>
                                                                    {expired && <Chip label="Đã hết hạn" color="error" size="small" variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />}
                                                                </Box>
                                                            ) : '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: 'white', display: { xs: 'none', sm: 'table-cell' } }}>
                                                            {formatDate(order.createdAt)}
                                                        </TableCell>
                                                        <TableCell>{getStatusChip(order.status)}</TableCell>
                                                        <TableCell align="right">
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                color="primary"
                                                                startIcon={<AutorenewIcon />}
                                                                onClick={handleRenewClick}
                                                            >
                                                                Gia Hạn
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )
                        ) : (
                            // TRANSACTION HISTORY TABLE
                            transactions.length === 0 ? (
                                <Typography textAlign="center" color="textSecondary">Chưa có giao dịch nào.</Typography>
                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ color: '#94a3b8' }}>Loại & Nội Dung</TableCell>
                                                <TableCell sx={{ color: '#94a3b8' }}>Số Tiền</TableCell>
                                                <TableCell sx={{ color: '#94a3b8' }}>Số Dư Sau GD</TableCell>
                                                <TableCell sx={{ color: '#94a3b8' }}>Thời Gian</TableCell>
                                                <TableCell sx={{ color: '#94a3b8' }}>Trạng Thái</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {transactions.map((trans) => (
                                                <TableRow key={trans.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                                                    <TableCell>
                                                        <Box display="flex" flexDirection="column">
                                                            <Typography fontWeight="bold" color={trans.type === 'DEPOSIT' ? '#4ade80' : '#f87171'}>
                                                                {trans.type === 'DEPOSIT' ? 'Nạp Tiền' : 'Thanh Toán'}
                                                            </Typography>
                                                            <Tooltip title="Mã nội dung (dùng để tra soát)">
                                                                <Typography variant="body2" color="#94a3b8" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(255,255,255,0.05)', p: 0.5, borderRadius: 1, width: 'fit-content' }}>
                                                                    {trans.description}
                                                                </Typography>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: trans.amount > 0 ? '#4ade80' : '#f87171' }}>
                                                        {trans.amount > 0 ? '+' : ''}{Number(trans.amount).toLocaleString()}đ
                                                    </TableCell>
                                                    <TableCell sx={{ color: 'white' }}>
                                                        {Number(trans.balanceAfter).toLocaleString()}đ
                                                    </TableCell>
                                                    <TableCell sx={{ color: '#94a3b8' }}>
                                                        {formatDate(trans.createdAt, true)}
                                                    </TableCell>
                                                    <TableCell>{getStatusChip(trans.status)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )
                        )}
                    </Box>
                </Paper>

                {/* Renewal Selection Modal */}
                <Dialog
                    open={showRenewModal}
                    onClose={() => setShowRenewModal(false)}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: {
                            bgcolor: '#0f172a',
                            color: 'white',
                            borderRadius: 3,
                            border: '1px solid rgba(255,255,255,0.1)',
                            backgroundImage: 'none',
                            position: 'relative',
                            minHeight: '650px' // Tăng chiều cao form
                        }
                    }}
                >
                    <IconButton
                        aria-label="close"
                        onClick={() => setShowRenewModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                            '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogTitle sx={{ textAlign: 'center', pb: 0, pt: 6 }}>
                        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ 
                            fontSize: { xs: '1.6rem', sm: '2rem', md: '3rem' },
                            background: 'linear-gradient(45deg, #60a5fa 30%, #a78bfa 90%)', 
                            WebkitBackgroundClip: 'text', 
                            WebkitTextFillColor: 'transparent', 
                            mb: 2 
                        }}>
                            Chọn Gói Gia Hạn
                        </Typography>
                        <Typography variant="h6" color="#94a3b8" sx={{ fontWeight: 'normal' }}>
                            Gia hạn gói cước để tiếp tục sử dụng các tính năng cao cấp
                        </Typography>
                    </DialogTitle>
                    <DialogContent sx={{ p: 6, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Grid container spacing={4} alignItems="stretch" justifyContent="center" sx={{ mt: 2 }}>
                            {packages.map((pkg) => (
                                <Grid item xs={12} sm={6} md={4} key={pkg.id}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            position: 'relative',
                                            bgcolor: 'transparent',
                                            backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0))',
                                            color: 'white',
                                            borderRadius: 4,
                                            border: '1px solid',
                                            borderColor: pkg.isPopular ? '#06b6d4' : 'rgba(255,255,255,0.1)',
                                            boxShadow: pkg.isPopular ? '0 0 15px rgba(6, 182, 212, 0.2)' : 'none',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.5)',
                                                borderColor: '#06b6d4'
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ flexGrow: 1, p: 3, textAlign: 'center' }}>
                                            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#cbd5e1', fontSize: '1.25rem' }}>
                                                {pkg.name}
                                            </Typography>

                                            <Box display="flex" alignItems="baseline" justifyContent="center" mb={0.5}>
                                                <Typography variant="h4" fontWeight="bold" sx={{ color: '#06b6d4' }}>
                                                    {Number(pkg.price).toLocaleString()}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#06b6d4', ml: 0.5, fontWeight: 'bold' }}>VND</Typography>
                                            </Box>

                                            <Typography variant="caption" color="#64748b" mb={3} display="block">
                                                Thời hạn: {pkg.durationDays} ngày
                                            </Typography>

                                            <Box display="flex" flexDirection="column" gap={1.5} textAlign="left" sx={{ pl: 1 }}>
                                                <Box display="flex" alignItems="center">
                                                    <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e', mr: 1 }} />
                                                    <Typography variant="body2" color="#94a3b8" sx={{ fontSize: '0.9rem' }}>Quản lý Host Account</Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center">
                                                    <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e', mr: 1 }} />
                                                    <Typography variant="body2" color="#94a3b8" sx={{ fontSize: '0.9rem' }}>Đồng bộ tự động</Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center">
                                                    <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e', mr: 1 }} />
                                                    <Typography variant="body2" color="#94a3b8" sx={{ fontSize: '0.9rem' }}>Hỗ trợ 24/7</Typography>
                                                </Box>
                                                {(Array.isArray(pkg.features) ? pkg.features : (typeof pkg.features === 'string' ? JSON.parse(pkg.features || '[]') : [])).map((feat, idx) => (
                                                    <Box key={idx} display="flex" alignItems="center">
                                                        <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e', mr: 1 }} />
                                                        <Typography variant="body2" color="#94a3b8" sx={{ fontSize: '0.9rem' }}>{feat}</Typography>
                                                    </Box>
                                                ))}
                                            </Box>

                                            {pkg.description && (
                                                <Typography variant="caption" color="#64748b" display="block" mt={3} fontStyle="italic" sx={{ opacity: 0.8 }}>
                                                    {pkg.description}
                                                </Typography>
                                            )}
                                        </CardContent>

                                        <Box p={3} pt={0}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                size="large"
                                                onClick={() => handleSelectPackage(pkg)}
                                                sx={{
                                                    borderColor: '#0f172a',
                                                    bgcolor: 'rgba(15, 23, 42, 0.6)',
                                                    color: '#06b6d4',
                                                    fontWeight: 'bold',
                                                    py: 1.5,
                                                    borderRadius: 2,
                                                    borderWidth: '1px',
                                                    boxShadow: 'inset 0 0 0 1px rgba(6, 182, 212, 0.2)',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(6, 182, 212, 0.1)',
                                                        color: '#06b6d4',
                                                        borderColor: '#06b6d4',
                                                        boxShadow: 'inset 0 0 0 1px #06b6d4'
                                                    }
                                                }}
                                            >
                                                Chọn Gói Này
                                            </Button>
                                        </Box>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </DialogContent>
                </Dialog>

                {/* Payment Modal */}
                {selectedPackage && (
                    <PaymentMethodModal
                        open={showPaymentModal}
                        onClose={handlePaymentClose}
                        pkg={selectedPackage}
                        currentBalance={balance}
                        onSuccess={handlePaymentSuccess}
                    />
                )}
            </Box>
        </HomeLayout>
    );
};

export default OrderHistoryPage;
